import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { OUTLINE_BRAND_BTN_CLASS } from "./constants";
import { cancelTodayLabel } from "./cancel-after-delivery-copy";
import { formatPrice } from "./formatters";

export interface CancelTodayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amount: number;
  onConfirm: () => void;
  confirmPending: boolean;
  /** Mutation succeeded: never re-submittable. */
  confirmSuccess?: boolean;
  /** Success is redirecting to an invoice (vs. cancelled immediately). */
  redirecting?: boolean;
}

export function CancelTodayDialog({
  open,
  onOpenChange,
  amount,
  onConfirm,
  confirmPending,
  confirmSuccess = false,
  redirecting = false,
}: CancelTodayDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Cancel today
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
        </DialogHeader>

        <p
          className="tw:text-sm tw:my-2"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {confirmSuccess && !redirecting
            ? "Your subscription has been cancelled."
            : amount > 0
              ? `You'll pay $${formatPrice(amount)} today and your subscription ends now.`
              : "Your subscription ends now."}
        </p>

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            disabled={confirmPending || (confirmSuccess && redirecting)}
            onClick={confirmSuccess && !redirecting ? () => onOpenChange(false) : onConfirm}
            className="tw:w-full tw:rounded-md tw:bg-[#523D33] tw:border-[#523D33] tw:text-[#FAC8CA] tw:hover:bg-[#523D33]/90 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]"
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : confirmSuccess && !redirecting ? (
              "Done"
            ) : (
              cancelTodayLabel(amount)
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
